import React, { useEffect, useRef } from 'react';
import { LocaleForm } from '../../LocaleForm/LocaleForm';
import { FlexGrid, getClassNameFactory, Tabs } from '@dcl/ui';
import ReactHtmlParser from 'react-html-parser';
import { ACCOUNTINGCS, ULTRATAX } from '../constants';
import { LeftNavArticle } from '../templates/leftNavArticle/LeftNavArticle';
import { ILocaleFormRef } from '../../LocaleForm/types';

interface PayrollTabsProps {
  stateData: any;
  localityDropdownLabel: string;
  localityButtonLabel: string;
  payrollType: string;
  localityDefaultSelectionLabel: string;
  selectedTab: string;
  selectLocalForm: string;
}

const getPayrollTemplate = (
  data,
  localityButtonLabel,
  localityDropdownLabel,
  payrollType,
  localityDefaultSelectionLabel,
  handleLocaleFormRender,
  isLocaleFormRendered,
  onClearLocaleForm,
  selectLocalForm,
  parentTabSwitchRef
) => {
  if (data.leftNav && data.leftNav.length > 0 && data.isLocalForm) {
    return (
      <>
        <LocaleForm
          localityButtonLabel={localityButtonLabel}
          localityDropdownLabel={localityDropdownLabel}
          localityDropdownData={data.localityDetails}
          localityPath={data.localPath}
          payrollType={payrollType}
          localityDefaultSelectionLabel={localityDefaultSelectionLabel}
          onLocaleFormRender={handleLocaleFormRender}
          onClearLocaleForm={onClearLocaleForm}
          selectLocalForm={selectLocalForm}
          ref={parentTabSwitchRef}
        />
        {!isLocaleFormRendered && (
          <LeftNavArticle
            leftNavContent={data.tabContent}
            leftNavItems={data.leftNav}
          ></LeftNavArticle>
        )}
      </>
    );
  } else if (data.leftNav && data.leftNav.length > 0) {
    return (
      <LeftNavArticle
        leftNavContent={data.tabContent}
        leftNavItems={data.leftNav}
      ></LeftNavArticle>
    );
  } else if (
    data.tabContent &&
    data.tabContent.length > 0 &&
    data.leftNav.length === 0 &&
    data.isLocalForm
  ) {
    return (
      <>
        <LocaleForm
          localityButtonLabel={localityButtonLabel}
          localityDropdownLabel={localityDropdownLabel}
          localityDropdownData={data.localityDetails}
          localityPath={data.localPath}
          payrollType={payrollType}
          localityDefaultSelectionLabel={localityDefaultSelectionLabel}
          onLocaleFormRender={handleLocaleFormRender}
          onClearLocaleForm={onClearLocaleForm}
          selectLocalForm={selectLocalForm}
          ref={parentTabSwitchRef}
        />
        {!isLocaleFormRendered && ReactHtmlParser(data.tabContent)}
      </>
    );
  } else if (data.isLocalForm) {
    return (
      <LocaleForm
        localityButtonLabel={localityButtonLabel}
        localityDropdownLabel={localityDropdownLabel}
        localityDropdownData={data.localityDetails}
        localityPath={data.localPath}
        payrollType={payrollType}
        localityDefaultSelectionLabel={localityDefaultSelectionLabel}
        onLocaleFormRender={handleLocaleFormRender}
        onClearLocaleForm={onClearLocaleForm}
        selectLocalForm={selectLocalForm}
        ref={parentTabSwitchRef}
      />
    );
  } else {
    return ReactHtmlParser(data.tabContent);
  }
};

export const PayRollTabs: React.FC<PayrollTabsProps> = (props) => {
  const {
    stateData,
    localityButtonLabel,
    localityDropdownLabel,
    payrollType,
    localityDefaultSelectionLabel,
    selectedTab,
    selectLocalForm,
  } = props;

  const getClassName = getClassNameFactory(PayRollTabs.displayName);
  const [isLocaleFormRendered, setIsLocaleFormRendered] = React.useState(false);

  const parentTabSwitchRef = useRef<ILocaleFormRef>(null);

  const removeHash = (panelId: string) => {
    const url = new URL(window.location.href);
    if (url.hash) {
      url.hash = '';
      window.history.replaceState({}, document.title, url.toString());
    }

    const urlWithParams = new URL(window.location.href);
    const tabIndex = panelId.replace('tab', '');
    const tabTitle = stateData.tabs[Number(tabIndex) - 1]?.tabTitle;

    if (urlWithParams.searchParams.has('hs_form')) {
      urlWithParams.searchParams.set('hs_form', tabTitle);
    } else {
      urlWithParams.searchParams.append('hs_form', tabTitle);
    }
    urlWithParams.searchParams.delete('hs_localforms');
    window.history.pushState(null, '', urlWithParams.toString());

    const container = document.querySelector(`#tab${tabIndex}-tab`);
    const clearButton = container?.querySelector(
      '[class$="Dropdown-clearButton"]'
    );
    if (clearButton) parentTabSwitchRef.current?.tabSwitchRef();
  };

  const handleLocaleFormRender = () => {
    setIsLocaleFormRendered(true);
  };

  const handleClearLocaleForm = () => {
    setIsLocaleFormRendered(false);
  };

  useEffect(() => {
    const urlWithParams = new URL(window.location.href);
    const tabIndex = selectedTab.replace('tab', '');
    const tabTitle = stateData.tabs[Number(tabIndex) - 1]?.tabTitle;
    if (!urlWithParams.searchParams.has('hs_form')) {
      urlWithParams.searchParams.append('hs_form', tabTitle);
    }
    window.history.pushState(null, '', urlWithParams.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  return (
    <FlexGrid.Col
      xs={12}
      lg={12}
      md={12}
      className={`${getClassName({ descendantName: 'payrollData' })} ${
        payrollType === ULTRATAX
          ? getClassName({ descendantName: 'ultratax' })
          : ''
      }`}
    >
      {stateData.tabs && (
        <Tabs
          layoutVariant={null}
          ariaLabelText="Tabs Information"
          dropdownLabelText={payrollType === ACCOUNTINGCS ? 'Showing' : 'Form'}
          enableArrowKeyCarousel={true}
          handleTabChange={removeHash}
          defaultTab={selectedTab}
        >
          {stateData.tabs.length > 0 &&
            stateData.tabs.map(
              (data: any, index: number) =>
                data.tabTitle && (
                  <Tabs.Panel
                    key={`tab${index + 1}`}
                    id={`tab${index + 1}`}
                    tabTitle={data.tabTitle}
                    segment={`segment-${index + 1}`}
                    subSegment={`subsegment-${index + 1}`}
                  >
                    {data &&
                      getPayrollTemplate(
                        data,
                        localityButtonLabel,
                        localityDropdownLabel,
                        payrollType,
                        localityDefaultSelectionLabel,
                        handleLocaleFormRender,
                        isLocaleFormRendered,
                        handleClearLocaleForm,
                        selectLocalForm,
                        parentTabSwitchRef
                      )}
                  </Tabs.Panel>
                )
            )}
        </Tabs>
      )}
    </FlexGrid.Col>
  );
};

PayRollTabs.displayName = 'PayRollTabs';
